import { extractWorkvivoUserIdFromLogMessage, parseProvisionLogMessage } from "./parseProvisionLogMessage";
import type { ProvisionJobLog } from "./types";

/**
 * Remove line breaks inside a cell so spreadsheet apps (Excel) don’t stretch row height.
 * Log bodies often contain `\n`; RFC 4180 allows newlines in quoted fields but they look broken in grids.
 */
function flattenForCsv(value: string): string {
  return String(value).replace(/\r\n|\n|\r/g, " ").trim();
}

function escapeCsvField(value: string): string {
  const flat = flattenForCsv(value);
  if (/[",\r\n]/.test(flat)) {
    return `"${flat.replace(/"/g, '""')}"`;
  }
  return flat;
}

/** Normalize parsed context keys for stable CSV column names (matches pipe-separated log keys). */
function normalizeContextKey(displayKey: string): string {
  return displayKey.trim().replace(/\s+/g, "_").toLowerCase();
}

function contextValueMap(parsed: ReturnType<typeof parseProvisionLogMessage>): Map<string, string> {
  const m = new Map<string, string>();
  for (const { key, value } of parsed.contextFields) {
    m.set(normalizeContextKey(key), value);
  }
  return m;
}

/** RFC 4180-ish CSV rows with parsed context columns plus details and raw message. */
export function buildProvisionLogsCsvRows(logs: ProvisionJobLog[]): string[] {
  const parsedLogs = logs.map((log) => ({
    log,
    parsed: parseProvisionLogMessage(log.message)
  }));

  const contextHeaderKeys = new Set<string>();
  for (const { parsed } of parsedLogs) {
    for (const { key } of parsed.contextFields) {
      contextHeaderKeys.add(normalizeContextKey(key));
    }
  }
  const sortedCtx = [...contextHeaderKeys].sort();

  const header = [
    "status",
    "log_type",
    "timestamp_iso",
    "spreadsheet_row",
    "workvivo_user_id",
    ...sortedCtx.map((k) => `context_${k}`),
    "details",
    "message_raw"
  ];
  const lines = [header.map(escapeCsvField).join(",")];

  for (const { log, parsed } of parsedLogs) {
    const rowNum =
      parsed.spreadsheetRow !== undefined ? String(parsed.spreadsheetRow) : "";
    const ts = log.timestamp || "";
    const workvivoUserId = extractWorkvivoUserIdFromLogMessage(log.message);
    const detailsJoined =
      parsed.details.length > 0
        ? parsed.details.join(" | ")
        : parsed.rawFallback && !parsed.details.length
          ? parsed.rawFallback
          : "";
    const msg = log.message ?? "";
    const ctx = contextValueMap(parsed);

    const cells: string[] = [
      log.status,
      log.logType,
      ts,
      rowNum,
      workvivoUserId,
      ...sortedCtx.map((k) => ctx.get(k) ?? ""),
      detailsJoined,
      msg
    ];
    lines.push(cells.map((cell) => escapeCsvField(String(cell))).join(","));
  }
  return lines;
}

export function downloadProvisionLogsCsv(logs: ProvisionJobLog[], filenameBase: string): void {
  if (logs.length === 0) return;
  const csv = `\uFEFF${buildProvisionLogsCsvRows(logs).join("\r\n")}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenameBase}-logs.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
